import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, EmptyState } from "@/components/Shell";
import { ChevronLeft, Package, Box } from "lucide-react";

export default function StockDetail() {
  const { warehouseId, productId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const showPrices = user?.role === "admin";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/stock/detail/${warehouseId}/${productId}`).then(r => setData(r.data)).catch(e => setErr(formatApiError(e)));
  }, [warehouseId, productId]);

  if (err) return <div className="p-6 text-rose-600 text-sm">{err}</div>;
  if (!data) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;

  const { product, warehouse, buckets, serials, issued_or_used_serials, movements } = data;

  return (
    <div className="p-4 md:p-8" data-testid="stock-detail-page">
      <button onClick={()=>nav(-1)} className="text-xs text-zinc-500 hover:text-[#F97316] mb-3 inline-flex items-center gap-1"><ChevronLeft size={14}/> Back to stock</button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#F97316]">{warehouse.name}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{product.name}</h1>
          <div className="text-sm text-zinc-500 mt-1 num">{product.sku} · {product.uom} · {product.serialized ? "Serialized" : "Non-serialized"}</div>
        </div>
        {showPrices && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Rate</div>
            <div className="font-display font-bold text-2xl num">₹{product.rate || 0}</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {buckets.length === 0 ? <Card className="p-5 col-span-4 text-center text-zinc-500 text-sm">No stock here yet.</Card> :
          buckets.map(b => (
            <Card key={b.bucket} className="p-5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{b.bucket}</div>
              <div className="font-display font-bold text-3xl num mt-1">{b.quantity}</div>
              {showPrices && b.bucket === "available" && <div className="text-xs text-zinc-500 mt-2 num">Value ₹{(b.quantity * (product.rate || 0)).toLocaleString("en-IN")}</div>}
            </Card>
          ))
        }
      </div>

      {product.serialized && (
        <>
          <Card className="p-5 mb-6">
            <div className="font-display font-semibold mb-3">Serial Numbers in Stock <span className="text-zinc-400 font-normal text-sm">({serials.length})</span></div>
            {serials.length === 0 ? <EmptyState title="No serials in this warehouse" icon={Package} /> :
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm data-table">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr><th className="px-3 py-2 text-left">Serial #</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Condition</th><th className="px-3 py-2 text-left">Added</th></tr>
                  </thead>
                  <tbody>
                    {serials.map(s => (
                      <tr key={s.id} className="border-t border-zinc-100">
                        <td className="px-3 py-2 num font-semibold">{s.serial_no}</td>
                        <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-zinc-100 text-zinc-700">{(s.status||"").replace(/_/g," ")}</span></td>
                        <td className="px-3 py-2 text-zinc-500">{s.condition || "—"}</td>
                        <td className="px-3 py-2 num text-xs text-zinc-500">{(s.created_at||"").slice(0,10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
          </Card>

          {issued_or_used_serials.length > 0 && (
            <Card className="p-5 mb-6">
              <div className="font-display font-semibold mb-3">Issued / Used Serials From This Warehouse <span className="text-zinc-400 font-normal text-sm">({issued_or_used_serials.length})</span></div>
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-sm data-table">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr><th className="px-3 py-2 text-left">Serial #</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Issued To</th><th className="px-3 py-2 text-left">Project</th></tr>
                  </thead>
                  <tbody>
                    {issued_or_used_serials.map(s => (
                      <tr key={s.id} className="border-t border-zinc-100">
                        <td className="px-3 py-2 num font-semibold">{s.serial_no}</td>
                        <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-zinc-100 text-zinc-700">{s.status}</span></td>
                        <td className="px-3 py-2">{s.issued_to_name || "—"}</td>
                        <td className="px-3 py-2 num text-xs">{s.project_id || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      <Card className="p-5">
        <div className="font-display font-semibold mb-3">Recent Movements</div>
        {movements.length === 0 ? <EmptyState title="No movements yet" icon={Box} /> :
          <table className="w-full text-sm data-table">
            <thead className="bg-zinc-50"><tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Kind</th><th className="px-3 py-2 text-left">Bucket</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-left">Ref</th></tr></thead>
            <tbody>
              {movements.map((m,i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="px-3 py-2 num text-xs">{(m.created_at||"").slice(0,19).replace("T"," ")}</td>
                  <td className="px-3 py-2 text-zinc-500 capitalize">{(m.kind||"").replace(/_/g," ")}</td>
                  <td className="px-3 py-2 text-zinc-500">{m.bucket}</td>
                  <td className={`px-3 py-2 num text-right font-bold ${m.quantity<0?"text-rose-600":"text-emerald-600"}`}>{m.quantity>0?"+":""}{m.quantity}</td>
                  <td className="px-3 py-2 num text-xs text-zinc-500">{m.ref_type} {m.ref_id?.slice(0,8)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </Card>
    </div>
  );
}
